import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PaymentMethodSelector } from "./PaymentMethodSelector";
import { registerPayment } from "@/hooks/usePaymentMethods";
import { toast } from "sonner";
import { Euro, CreditCard, FileText, Calendar, User } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface PaymentRegistrationProps {
  bill: {
    id: string;
    total_amount: number;
    student?: {
      name: string;
      class: string;
    };
    activity?: {
      name: string;
    };
    invoice_date: string;
    due_date: string;
    sessions_attended: number;
    price_amount: number;
  };
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const PaymentRegistration = ({
  bill,
  isOpen,
  onClose,
  onSuccess
}: PaymentRegistrationProps) => {
  const [formData, setFormData] = useState({
    amount: bill.total_amount,
    payment_method_id: '',
    reference_number: '',
    notes: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.payment_method_id) {
      toast.error("Por favor selecciona un método de pago");
      return;
    }

    if (formData.amount <= 0) {
      toast.error("El importe debe ser mayor a 0");
      return;
    }

    if (formData.amount > bill.total_amount) {
      toast.error("El importe no puede ser mayor al total de la factura");
      return;
    }

    try {
      setIsProcessing(true);
      
      await registerPayment(bill.id, {
        amount: formData.amount,
        payment_method_id: formData.payment_method_id,
        reference_number: formData.reference_number || undefined,
        notes: formData.notes || undefined
      });

      toast.success("Pago registrado correctamente");
      
      // Cerrar modal primero para evitar problemas de estado
      onClose();
      
      // Llamar onSuccess con un pequeño delay para permitir que se cierre el modal
      setTimeout(() => {
        onSuccess();
      }, 100);
      
    } catch (error: any) {
      console.error('Error registering payment:', error);
      toast.error(`Error al registrar el pago: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setFormData({
      amount: bill.total_amount,
      payment_method_id: '',
      reference_number: '',
      notes: ''
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open && !isProcessing) {
        onClose();
      }
    }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" />
            Registrar Pago
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Información de la factura */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Información de la Factura
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{bill.student?.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {bill.student?.class}
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {bill.activity?.name}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">
                      Vencimiento: {format(new Date(bill.due_date), 'dd/MM/yyyy', { locale: es })}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {bill.sessions_attended} sesiones × {bill.price_amount.toFixed(2)}€
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-primary/10 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Total a pagar:</span>
                  <div className="flex items-center gap-1">
                    <Euro className="w-4 h-4 text-primary" />
                    <span className="text-xl font-bold text-primary">
                      {bill.total_amount.toFixed(2)}€
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Formulario de pago */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Importe *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  max={bill.total_amount}
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="payment_method">Método de Pago *</Label>
                <PaymentMethodSelector
                  value={formData.payment_method_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, payment_method_id: value }))}
                  placeholder="Seleccionar método"
                  className="w-full"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reference_number">Número de Referencia</Label>
              <Input
                id="reference_number"
                value={formData.reference_number}
                onChange={(e) => setFormData(prev => ({ ...prev, reference_number: e.target.value }))}
                placeholder="Ej: TXN123456, REF789, etc."
              />
              <p className="text-sm text-muted-foreground">
                Opcional: Número de transacción, referencia bancaria, etc.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notas</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Notas adicionales sobre el pago..."
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                type="submit" 
                disabled={isProcessing}
                className="flex-1"
              >
                {isProcessing ? 'Procesando...' : 'Registrar Pago'}
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleReset}
                disabled={isProcessing}
              >
                Limpiar
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                disabled={isProcessing}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentRegistration;
